// pattern: imperative-shell
import { STAGES } from "../core/observation-model";
import type { ApiLocation, ApiObservation, ApiQueryResult } from "../shell/api-client";
import { api } from "../shell/api-client";
import { renderObservationCard } from "./observation-card";

export async function renderSearchPage(container: HTMLElement): Promise<void> {
	container.innerHTML = '<div class="loading">Loading...</div>';

	try {
		const locations = await api.getLocations();
		renderSearch(container, locations);
	} catch (e) {
		container.innerHTML = `<div class="content"><div class="error">${e instanceof Error ? e.message : "unknown error"}</div></div>`;
	}
}

function renderSearch(container: HTMLElement, locations: ApiLocation[]): void {
	const locationOptions = locations
		.map((l) => `<option value="${l.id}">${esc(l.name)}</option>`)
		.join("");

	const stageOptions = STAGES.map((s) => `<option value="${s}">${s}</option>`).join("");

	container.innerHTML = `
		<div class="content">
			<div class="nl-section">
				<div class="nl-header">
					<span class="form-label">Ask a question</span>
				</div>
				<textarea class="form-textarea" id="nl-query"
					placeholder="e.g. What was blooming in the shade garden last May?"></textarea>
				<button class="btn btn-primary btn-full" type="button" id="nl-query-btn" style="margin-top:8px">
					Ask
				</button>
				<div id="nl-query-result" style="margin-top:12px"></div>
				<div class="nl-divider"><span>or filter manually</span></div>
			</div>

			<form id="search-form">
				<div class="form-group">
					<label class="form-label" for="s-plant">Plant</label>
					<input class="form-input" type="text" id="s-plant" name="plant" placeholder="Search by plant name..." />
				</div>
				<div class="form-group">
					<label class="form-label" for="s-location">Location</label>
					<select class="form-select" id="s-location" name="location_id">
						<option value="">All locations</option>
						${locationOptions}
					</select>
				</div>
				<div class="form-group">
					<label class="form-label" for="s-stage">Stage</label>
					<select class="form-select" id="s-stage" name="stage">
						<option value="">All stages</option>
						${stageOptions}
					</select>
				</div>
				<div style="display: flex; gap: 8px">
					<div class="form-group" style="flex:1">
						<label class="form-label" for="s-from">From</label>
						<input class="form-input" type="date" id="s-from" name="from" />
					</div>
					<div class="form-group" style="flex:1">
						<label class="form-label" for="s-to">To</label>
						<input class="form-input" type="date" id="s-to" name="to" />
					</div>
				</div>
				<button class="btn btn-primary btn-full" type="submit">Search</button>
			</form>

			<div id="search-results" style="margin-top: 16px"></div>
		</div>
	`;

	wireNLQuery(container);
	wireFilterForm(container);
}

function wireNLQuery(container: HTMLElement): void {
	const queryInput = container.querySelector<HTMLTextAreaElement>("#nl-query")!;
	const queryBtn = container.querySelector<HTMLButtonElement>("#nl-query-btn")!;
	const resultEl = container.querySelector<HTMLElement>("#nl-query-result")!;

	queryBtn.addEventListener("click", async () => {
		const question = queryInput.value.trim();
		if (!question) {
			resultEl.innerHTML = '<div class="error">Type a question first</div>';
			return;
		}

		queryBtn.disabled = true;
		resultEl.innerHTML = '<div class="loading">Thinking...</div>';

		try {
			const result = await api.queryNaturalLanguage(question);
			renderQueryResult(resultEl, result);
		} catch (e) {
			resultEl.innerHTML = `<div class="error">${e instanceof Error ? e.message : "Query failed"}</div>`;
		}

		queryBtn.disabled = false;
	});
}

function renderQueryResult(container: HTMLElement, result: ApiQueryResult): void {
	if (result.count === 0) {
		container.innerHTML = `
			<div class="card">
				<div class="observation-notes">${esc(result.explanation)}</div>
				<div class="empty-text" style="margin-top:8px">No matching results</div>
			</div>
		`;
		return;
	}

	// Render explanation + results
	container.innerHTML = `
		<div class="card" style="margin-bottom:12px">
			<div class="observation-notes">${esc(result.explanation)}</div>
			<div style="font-size:0.8rem;color:var(--gray-500);margin-top:4px">${result.count} result${result.count === 1 ? "" : "s"}</div>
		</div>
		<div id="nl-results"></div>
	`;

	const resultsEl = container.querySelector<HTMLElement>("#nl-results")!;

	// Try to render as observation cards if the results have the right shape
	for (const row of result.results) {
		if (row["plant_name"] && row["stage"]) {
			// Looks like an observation — render as card
			const card = renderObservationCard(row as unknown as ApiObservation);
			resultsEl.appendChild(card);
		} else {
			// Generic result — render as key/value card
			const card = document.createElement("div");
			card.className = "card";
			card.innerHTML = Object.entries(row)
				.filter(([, v]) => v !== null)
				.map(([k, v]) => `<div><strong>${esc(k)}:</strong> ${esc(String(v))}</div>`)
				.join("");
			resultsEl.appendChild(card);
		}
	}
}

function wireFilterForm(container: HTMLElement): void {
	const form = container.querySelector<HTMLFormElement>("#search-form")!;
	form.addEventListener("submit", async (e) => {
		e.preventDefault();
		const fd = new FormData(form);
		const entries: Array<[string, string]> = [];

		const plant = fd.get("plant") as string;
		if (plant) entries.push(["plant", plant]);

		const locationId = fd.get("location_id") as string;
		if (locationId) entries.push(["location_id", locationId]);

		const stage = fd.get("stage") as string;
		if (stage) entries.push(["stage", stage]);

		const from = fd.get("from") as string;
		if (from) entries.push(["from", from]);

		const to = fd.get("to") as string;
		if (to) entries.push(["to", to]);

		const params = Object.fromEntries(entries);

		const resultsEl = container.querySelector("#search-results")!;
		resultsEl.innerHTML = '<div class="loading">Searching...</div>';

		try {
			const results = await api.getObservations(params);
			renderFilterResults(resultsEl as HTMLElement, results);
		} catch (err) {
			resultsEl.innerHTML = `<div class="error">${err instanceof Error ? err.message : "Search failed"}</div>`;
		}
	});
}

function renderFilterResults(container: HTMLElement, results: ApiObservation[]): void {
	if (results.length === 0) {
		container.innerHTML =
			'<div class="empty"><div class="empty-text">No matching observations</div></div>';
		return;
	}

	container.innerHTML = `<div style="font-size:0.85rem;color:var(--gray-500);margin-bottom:8px">${results.length} result${results.length === 1 ? "" : "s"}</div>`;
	for (const obs of results) {
		container.appendChild(renderObservationCard(obs));
	}
}

function esc(s: string): string {
	const el = document.createElement("span");
	el.textContent = s;
	return el.innerHTML;
}
